import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../dataManagement/uiDb/UiDb";
import {
  selectDineInSession,
  selectDineInRounds,
  selectDineInRoundItems,
  selectDineInMembers,
  selectDineInProducts,
  selectDineInBill,
  selectDineInLoadingMap,
} from "../../../dataManagement/uiDb/DineInDb";
import DineInThunk from "../../../services/thunks/DineInThunk";
import {
  findOpenRound,
  totalBillAmount,
  totalPlateCount,
} from "../../../services/helpers/DineInSummaryHelper";
import { useDineInLiveSync } from "./hooks/useDineInLiveSync";
import DineInSessionHeader from "./childComponents/DineInSessionHeader";
import DineInFeedList from "./childComponents/DineInFeedList";
import DineInActionBar from "./childComponents/DineInActionBar";
import "./styles/DineInSession.css";
import { useChintuStory } from "./celebration/useChintuStory";
import ChintuPlacard from "./celebration/ChintuPlacard";
import "./celebration/ChintuPlacard.css";
// The placards are plain DOM/CSS and ship with the page. Only the 3D finale
// (three.js + the 1.1MB model) is split out — useChintuStory prewarms this
// same chunk the moment Chintu gets invited.
const ChintuCelebration = React.lazy(() => import("./celebration/ChintuCelebration"));

interface DineInSessionProps {
  businessName: string;
}

const DineInSession: React.FC<DineInSessionProps> = ({ businessName }) => {
  const dispatch = useDispatch<AppDispatch>();
  const session = useSelector(selectDineInSession);
  const rounds = useSelector(selectDineInRounds);
  const roundItems = useSelector(selectDineInRoundItems);
  const members = useSelector(selectDineInMembers);
  const products = useSelector(selectDineInProducts);
  const currentUserId = useSelector((state: RootState) => state.profile.data?.userId);
  const bill = useSelector(selectDineInBill);
  const isGeneratingBill = !!useSelector(selectDineInLoadingMap).generateBill;

  // Resolve name/price for every product referenced so far — re-fires
  // whenever a productId shows up that hasn't been fetched yet.
  useEffect(() => {
    const knownIds = new Set(products.map((p) => p.productId));
    const missingIds = Array.from(new Set(roundItems.map((i) => i.productId))).filter(
      (id) => !knownIds.has(id)
    );
    if (missingIds.length > 0) dispatch(DineInThunk.fetchDineInProducts(missingIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundItems]);

  const openRound = useMemo(() => findOpenRound(rounds), [rounds]);
  // Plates, not rows. Rows stopped being a meaningful count once the converter
  // stopped aggregating, and this gates the "Order" button — a round holding
  // nothing but items the kitchen rejected must not look orderable, which
  // totalPlateCount handles by excluding them.
  const openRoundItemCount = useMemo(
    () =>
      openRound
        ? totalPlateCount(roundItems.filter((i) => i.roundId === openRound.roundId))
        : 0,
    [openRound, roundItems]
  );
  const billTotal = useMemo(() => totalBillAmount(roundItems, products), [roundItems, products]);

  // Resiliency net for the FCM push wiring — see useDineInLiveSync's doc
  // comment for why a backgrounded browser tab needs this. Called
  // unconditionally (rules of hooks) with session?.sessionId, which the hook
  // itself treats as a no-op until it's defined.
  useDineInLiveSync(session?.sessionId);

  // Chintu's arc: placards at ₹500 and ₹1,500, and — depending on how the
  // deciding placard was answered — the 3D arrival at ₹2,000. Thresholds,
  // copy and the arrival rule all live in celebration/chintuConfiguration.ts.
  // billLocked: while the bill is being generated or shown, a milestone
  // queues instead of landing on top of it.
  const chintu = useChintuStory({
    sessionId: session?.sessionId,
    rounds,
    roundItems,
    products,
    billLocked: !!bill || isGeneratingBill,
  });

  if (!session) return null;

  return (
    <div className="dine-in-session">
      {/* The shake wraps the page CONTENT, never Chintu — he hangs steady
          while the app rattles under him, which is the whole joke. The
          transform makes this a containing block for position:fixed
          children, so it's only applied while a shake-tier placard is up.
          __content carries the flex-column/flex:1 that .dine-in-session
          applies, so the action bar still sits at the bottom. */}
      <div
        className={`dine-in-session__content${chintu.shake ? " chintu-shake" : ""}`}
      >
        <DineInSessionHeader
          businessName={businessName}
          tableNumber={session.tableNumber}
          members={members}
          creatorId={session.creatorId}
          currentUserId={currentUserId}
          billTotal={billTotal}
          sessionCode={session.sessionCode}
        />
        <DineInFeedList />
        <DineInActionBar
          businessId={session.businessId}
          businessName={businessName}
          openRound={openRound}
          openRoundItemCount={openRoundItemCount}
        />
      </div>

      {chintu.shake && <div className="chintu-edge-glow" />}

      {chintu.placard && (
        <ChintuPlacard
          placardId={chintu.placard}
          answers={chintu.answers}
          amount={chintu.amount}
          onAnswer={chintu.answer}
          onClose={chintu.closePlacard}
        />
      )}

      {chintu.celebrating && (
        <React.Suspense fallback={null}>
          <ChintuCelebration amount={chintu.amount} onDone={chintu.endCelebration} />
        </React.Suspense>
      )}
    </div>
  );
};

export default DineInSession;
